import {
  BaseEquipment,
  EnabledFeatures,
  Equipment,
  EquipmentRequiringInstruction,
  Gender,
  GymTypes,
  IEnabledFeatures,
  SmallBodyGroupType,
  smallBodyGroupTypesArray,
  StrengthRatioForMuscleGroups,
} from 'fitbeat.models';
import { UserProfile } from '../../../entities';
import { DbGymRepository } from '../../../persistence';
import { getEquipmentResistanceStrategy } from '../../equipment-weight-recommendations/EquipmentResistanceStrategy';
import { defaultStrengthRatioForFemales, homeEquipmentWeightInfoMapping } from '../../workouthydration';
import {
  CompleteAssessment,
  dumbbellStregthAssessmentSteps,
  ExplainAssessment,
  ExplainerVideo,
  GeneralStrengthLevel,
  IExplainerVideoStep,
  ManualSelection,
  manualStrengthAssessmentSteps,
  SelectDumbbellWeight,
  SelectWeight,
  SetUpHomeGym,
  StartAssessment,
  StartAssessmentElite,
  StrengthAssessmentSteps,
  StrengthSettings,
  uniBandstregthAssessmentSteps,
  WeightAsessmentSteps,
} from './config';

export interface IAsessmentFlows {
  manualFlow: StrengthAssessmentSteps;
  uniBandsFlow: StrengthAssessmentSteps;
  dumbbellFlow: StrengthAssessmentSteps;
  userStrengthRatios: StrengthRatioForMuscleGroups;
}
interface ILegacyAssessmentFlows {
  stregthAssessmentSteps: StrengthAssessmentSteps;
}

export type AssessmentData = ILegacyAssessmentFlows | IAsessmentFlows;

export class StrengthAssessmentManager {
  public static buildStrengthAssessmentConfig(
    user: UserProfile,
    enabledFeatures: IEnabledFeatures,
  ): AssessmentData {
    const manualFlow = StrengthAssessmentManager.buildStrengthAssessmentSteps(
      manualStrengthAssessmentSteps,
      user,
      enabledFeatures,
      undefined,
    );
    let uniBandsFlow: StrengthAssessmentSteps = [];
    let dumbbellFlow: StrengthAssessmentSteps = [];
    uniBandsFlow = StrengthAssessmentManager.buildStrengthAssessmentSteps(
      uniBandstregthAssessmentSteps,
      user,
      enabledFeatures,
      Equipment.UniversalBands,
    );

    dumbbellFlow = StrengthAssessmentManager.buildStrengthAssessmentSteps(
      dumbbellStregthAssessmentSteps,
      user,
      enabledFeatures,
      Equipment.Dumbbells,
    );

    const userStrengthRatios = StrengthAssessmentManager.getUserStrengthRatios(user);
    return {
      manualFlow,
      uniBandsFlow,
      dumbbellFlow,
      userStrengthRatios,
    };
  }

  public static getStrengthRatioForPercentage(
    userProfile: UserProfile,
  ): (value: number) => number {
    const isMale = userProfile.gender === Gender.Male;
    const adjustment = isMale ? 1 : 0.67;
    const ratioIncreasePerTenPercent = 0.02;
    const equation = (value: number) => {
      const res = ratioIncreasePerTenPercent * value * adjustment;
      const correctToTwoDecimals = Math.round(res * 100) / 100;
      return correctToTwoDecimals;
    };
    return equation;
  }

  public static getPercentageForStrengthRatio(
    userProfile: UserProfile,
  ): (value: number) => number {
    const isMale = userProfile.gender === Gender.Male;
    const femaleToMaleAverageStrengthRatio = 0.67;
    const adjustment = isMale ? 1 : femaleToMaleAverageStrengthRatio;
    const strengthIncreasePerPercent = 50;
    const equation = (ratio: number) => {
      const roundedValue = Math.round(
        (ratio / adjustment) * strengthIncreasePerPercent,
      );
      const boundedValue = Math.max(0, Math.min(100, roundedValue));
      return boundedValue;
    };
    return equation;
  }

  private static getUserStrengthRatios(user: UserProfile) {
    if (user.strengthRatioForMuscleGroups) {
      return user.strengthRatioForMuscleGroups;
    }
    if (user.gender === Gender.Female) {
      return smallBodyGroupTypesArray.reduce(
        (acc: StrengthRatioForMuscleGroups, bodyGroup: SmallBodyGroupType) => {
          acc[bodyGroup] = defaultStrengthRatioForFemales;
          return acc;
        },
        {} as StrengthRatioForMuscleGroups,
      );
    }
    return smallBodyGroupTypesArray.reduce(
      (acc: StrengthRatioForMuscleGroups, bodyGroup: SmallBodyGroupType) => {
        acc[bodyGroup] = 1;
        return acc;
      },
      {} as StrengthRatioForMuscleGroups,
    );
  }

  private static shouldShowProgressBar = (step: WeightAsessmentSteps) => {
    const stepsThatShouldHideProgressBar = [
      GeneralStrengthLevel,
      ManualSelection,
      StrengthSettings,
      StartAssessmentElite,
      ExplainAssessment,
      StartAssessment,
      CompleteAssessment,
    ];
    return !stepsThatShouldHideProgressBar.includes(step.type);
  };

  private static buildStrengthAssessmentSteps(
    steps: WeightAsessmentSteps[],
    user: UserProfile,
    enabledFeature: IEnabledFeatures,
    equipment?: EquipmentRequiringInstruction,
  ) {
    const assessmentSteps = steps.map((step) => {
      let route = step.type;
      const isExplainStep = step.type === ExplainerVideo;
      const isExplainOrSelectStep = step.type === SelectWeight || isExplainStep || step.type === SelectDumbbellWeight;
      if (isExplainOrSelectStep) {
        route += `-${step.primaryMuscleMover}`;
      }
      if (isExplainStep && equipment) {
        step.recommendation = StrengthAssessmentManager.getRecommendedTextAndWeight(user, step, equipment);
      }
      step.shouldShowProgressBar = StrengthAssessmentManager.shouldShowProgressBar(step);
      return {
        config: step,
        questionId: route,
        route,
      };
    });

    const userHasStrengthRatios = !!user.strengthRatioForMuscleGroups;
    const shouldShowShortStrengthAssessmentFlow = !userHasStrengthRatios
      && (
        EnabledFeatures.hasAccessToAStudioGym(enabledFeature)
        || BaseEquipment.isHomeDumbbellsEquipmentSelection(user.defaultEquipment)
      );
    if (shouldShowShortStrengthAssessmentFlow) {
      const titleKey = 'generalStrengthLevelTitleKey';
      assessmentSteps.unshift({
        config: {
          titleKey,
          descriptionKey: 'generalStrengthLevelDescription',
          type: GeneralStrengthLevel,
          buttonKey: '',
        },
        route: GeneralStrengthLevel,
        questionId: GeneralStrengthLevel,
      }, {
        config: {
          titleKey: 'strengthSettingsTitle',
          descriptionKey: 'strengthSettingsDescription',
          type: StrengthSettings,
          buttonKey: 'strengthSettingsBtnKey',
        },
        route: StrengthSettings,
        questionId: StrengthSettings,
      });
    }

    const shouldShowSetupHomeGymStep = !userHasStrengthRatios
      && BaseEquipment.isHomeBenchBandsEquipmentSelection(user.defaultEquipment);
    if (shouldShowSetupHomeGymStep) {
      assessmentSteps.unshift({
        config: {
          titleKey: 'setupHomeGym',
          type: SetUpHomeGym,
          buttonKey: 'startStrengthAssessment',
        },
        route: SetUpHomeGym,
        questionId: SetUpHomeGym,
      });
    }

    if (userHasStrengthRatios) {
      assessmentSteps.unshift({
        config: {
          titleKey: 'strengthSettingsTitle',
          descriptionKey: 'strengthSettingsDescription',
          type: StrengthSettings,
          buttonKey: 'strengthSettingsBtnKey',
        },
        route: StrengthSettings,
        questionId: StrengthSettings,
      });
    }

    return assessmentSteps;
  }

  private static getRecommendedTextAndWeight(
    user: UserProfile,
    step: IExplainerVideoStep,
    equipment: EquipmentRequiringInstruction,
  ) {
    const mapping = homeEquipmentWeightInfoMapping.get(
      equipment,
    )!;

    const userGymType = user.gymName ? DbGymRepository.getByName(user.gymName).type : GymTypes.Indoor;
    const resistanceStrategy = getEquipmentResistanceStrategy(userGymType, equipment);
    const strengthRatios = user.strengthRatioForMuscleGroups;
    const currentSetting = strengthRatios && strengthRatios[step.primaryMuscleMover];
    if (Number(currentSetting) >= 0 && strengthRatios) {
      const suggestedStartingWeight = resistanceStrategy.getEquipmentWeight(
        equipment,
        step.exerciseId,
        step.equipmentWeightLevel,
        step.primaryMuscleMover,
        user,
      );
      return {
        text: 'Current Setting',
        suggestedStartingWeight: suggestedStartingWeight.weight,
        ratio: strengthRatios[step.primaryMuscleMover],
        primaryMuscleMover: step.primaryMuscleMover,
      };
    }
    if (user.gender === Gender.Male) {
      const suggestedStartingWeightMale = mapping.get(
        step.equipmentWeightLevel,
      )?.weight;
      return {
        text: 'Suggested starting weight',
        suggestedStartingWeight: suggestedStartingWeightMale,
        ratio: 1,
        primaryMuscleMover: step.primaryMuscleMover,
      };
    }
    const adjustedEquipmnetWeightForFemale = Math.round(
      step.equipmentWeightLevel * defaultStrengthRatioForFemales,
    );
    const suggestedStartingWeightFemale = mapping.get(
      adjustedEquipmnetWeightForFemale,
    )?.weight;
    return {
      text: 'Suggested starting weight',
      suggestedStartingWeight: suggestedStartingWeightFemale,
      ratio: 0.8,
      primaryMuscleMover: step.primaryMuscleMover,
    };
  }
}
